/**
 * AI 운영자 NPC 도슨트 — FR-NPC. 1단계(버튼) + 2단계(LLM 프록시) 공유 로직.
 * 가드레일(FR-NPC-03): 범위 한정 + 외부URL 생성 차단 + 개인정보 마스킹. 비용 상한(FR-NPC-04).
 */
import { maskText } from "./filter";
import { detectLanguage, retrieveChunks, LANG_NAME, type KbChunk, type Lang } from "./ai";

// 경량 모델(Haiku 계열) — 비용·지연 관리(기획서 §6). 정확한 모델 id.
export const NPC_MODEL = "claude-haiku-4-5";
export const NPC_MAX_TOKENS = 512; // 짧은 안내 — 비용 절감
export const NPC_INPUT_MAXLEN = 500; // 모델 전송 전 입력 절단(프롬프트 주입·비용 방어)

export interface NpcButton {
  id: string;
  label: string;
  answer: string;
}
export interface NpcConfig {
  scope: string;
  outOfScope: string;
  buttons: NpcButton[];
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" && v.length > 0 ? v : fallback;
}

export function normalizeNpc(input: unknown): NpcConfig {
  const o = (input ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(o.buttons) ? o.buttons : [];
  const buttons: NpcButton[] = raw
    .map((b): NpcButton | null => {
      if (!b || typeof b !== "object") return null;
      const bt = b as Record<string, unknown>;
      const id = str(bt.id);
      const label = str(bt.label);
      const answer = str(bt.answer);
      if (!id || !label || !answer) return null;
      return { id, label, answer };
    })
    .filter((b): b is NpcButton => b !== null);
  return {
    scope: str(o.scope, "전자책·메타버스·AR·굿즈·이벤트"),
    outOfScope: str(
      o.out_of_scope ?? o.outOfScope,
      "개인정보, 의료 조언, 무섭거나 폭력적인 내용은 도와드리기 어려워요. 다른 이야기는 함께해요!",
    ),
    buttons,
  };
}

export function findButtonAnswer(config: NpcConfig, id: string): string | null {
  return config.buttons.find((b) => b.id === id)?.answer ?? null;
}

/** 2단계 도슨트 시스템 프롬프트(가드레일) — FR-NPC-03. */
export function buildDocentSystemPrompt(config: NpcConfig): string {
  return [
    "너는 '그레그와 병아리의 경주 그림책방'의 친절한 안내 요정이야. 영유아와 보호자에게 존댓말로, 2~3문장 이내로 짧고 쉽게 답해.",
    `특히 잘 아는 주제는 다음이야: ${config.scope}. 참고 자료에 질문과 관련된 내용이 있으면 그 사실을 사용해 바로 답해.`,
    "이 전문 주제에 대화를 가두지 마. 일반 상식·학교·놀이·감정·취향·문화 등 안전한 질문과 일상 대화에는 웬만하면 자연스럽게 답해.",
    "질문에는 반드시 먼저 답을 말해. 인사와 질문이 함께 있으면 짧게 인사한 뒤 질문에 답해. 되묻기만 하거나 전문 주제 목록만 반복하면 안 돼.",
    "무엇을 뜻하는지 전혀 알 수 없는 입력에만 한 번 짧게 다시 말해 달라고 해.",
    `다음 세 가지에는 위험한 내용이나 조언을 제공하지 말고 '${config.outOfScope}'라고 안내해: 개인정보 요청·추측·복원·생성, 의료 진단·치료·복약 조언, 폭력·위험 행동을 돕는 방법이나 무섭고 잔혹한 묘사.`,
    "외부 링크/URL, 전화번호·주소·이메일 같은 개인정보는 만들거나 요청하지 마.",
    "모르면 모른다고 솔직히 말하고 운영자에게 물어보라고 안내해.",
  ].join("\n");
}

/**
 * 2단계 도슨트 LLM 요청 준비(순수·테스트 가능) — 아동 안전 핵심 로직.
 * SDK 호출 직전까지의 모든 가공을 한 곳에 모아 키 없이도 검증 가능하게 한다:
 *  1) 입력 PII 마스킹 + 길이 절단(프롬프트 주입 방어, FR-NPC-03)
 *  2) 질문 언어 감지 → 같은 언어 응답 지시(FR-AI-02)
 *  3) RAG-lite 근거 주입(범위 내 사실만, FR-AI-01)
 * 반환한 system/userText 를 그대로 모델에 보내고, 응답은 sanitizeOutbound 로 후처리한다.
 */
export interface DocentRequest {
  model: string;
  maxTokens: number;
  system: string;
  userText: string; // PII 마스킹·절단 완료(이대로 전송 안전)
  lang: Lang;
}

export function buildDocentRequest(
  text: string,
  config: NpcConfig,
  kb: KbChunk[] = [],
): DocentRequest {
  // ⚠️ 먼저 절단 후 마스킹 — maskText 가 무제한 길이를 보면 ADDRESS_RE O(n²) 로 이벤트루프 프리즈.
  const capped = (typeof text === "string" ? text : "").slice(0, NPC_INPUT_MAXLEN);
  const userText = maskText(capped).text;
  const lang = detectLanguage(userText);
  const chunks = retrieveChunks(kb, userText, 3);

  let system = buildDocentSystemPrompt(config);
  system += `\n반드시 ${LANG_NAME[lang]}(으)로만 답해.`;
  if (chunks.length > 0) {
    system +=
      "\n\n참고 자료(이 범위 안에서만 사실을 말해):\n" +
      chunks.map((c) => `- ${c.title}: ${c.text}`).join("\n");
  }
  return { model: NPC_MODEL, maxTokens: NPC_MAX_TOKENS, system, userText, lang };
}

/** 모델 출력 후처리: 생성된 URL 제거 + 개인정보 마스킹(FR-NPC-03). */
export function sanitizeOutbound(text: string): string {
  if (typeof text !== "string") return "";
  let out = text
    // 마크다운 링크 [텍스트](url) → 텍스트만
    .replace(/\[([^\]]+)\]\((?:https?:\/\/|www\.)[^)]+\)/gi, "$1")
    // 노출된 URL 제거
    .replace(/\b(?:https?:\/\/|www\.)\S+/gi, "[링크 생략]");
  out = maskText(out).text; // 전화·이메일·주소·금칙어 마스킹
  return out.trim();
}

/**
 * 💰 **AI 자유 대화 하루 상한** — 조치계획서 ■10 의 「월 상한 3만 원(하루 300회 기준)」이
 * 기대는 숫자다. 관리자 비용 계산과 엣지의 막는 자리가 **같은 값**을 봐야 해서 여기 둔다.
 *
 * 🔴 이 값을 지키는 것은 D1 의 날짜별 카운터다(`statCountToday`). 아래 `DailyLimiter` 는
 * 격리 공간 하나의 메모리라 **하루 전체를 못 센다** — D1 을 못 물어볼 때의 뒷받침일 뿐이다.
 */
export const NPC_ASK_DAILY_CAP = 300;

/**
 * 일 호출 상한 카운터(FR-NPC-04). 날짜 바뀌면 리셋. 초과 시 1단계로 폴백.
 * 인메모리(베스트에포트) — 다중 인스턴스 정밀 제한은 외부 저장소로 교체.
 *
 * 🔴 **엣지에서는 이것만으로 상한이 안 된다**(2026-09-25). 도슨트가 VM 에서 엣지로 옮겨 오면서
 * (docs/build/00 S4-4) 요청을 **여러 격리 공간**이 나눠 받고, 격리 공간은 수시로 새로 뜬다 —
 * 뜰 때마다 0 에서 시작한다. VM 한 대일 때는 맞던 가정이 조용히 깨졌다.
 * 진짜 상한은 D1 카운터가 지키고(`NPC_ASK_DAILY_CAP`), 이것은 D1 을 못 물어볼 때만 쓴다.
 */
export class DailyLimiter {
  private day = "";
  private count = 0;
  constructor(private limit: number) {}

  /** 오늘 호출 1회 시도. 허용되면 true(+카운트), 초과면 false. */
  tryConsume(today: string): boolean {
    if (today !== this.day) {
      this.day = today;
      this.count = 0;
    }
    if (this.count >= this.limit) return false;
    this.count += 1;
    return true;
  }

  remaining(today: string): number {
    if (today !== this.day) return this.limit;
    return Math.max(0, this.limit - this.count);
  }
}
